/* WaveSurfer is a non-React, pure-JavaScript libary. We use its Regions plugin to allow easy handling of our annotations.
When we load annotations from the backend API, we must create Region objects and add them to the Wavesurfer.
And when the user changes a region's starting/ending point or deletes it, we must update the backend to remove the corresponding annotation.
(Handled in audio-tool-event-handlers.tsx)

The 'Audio' wavesurfer is set to height 0 to be hidden, but still 'rendered' so that its corresponding Spectrogram plugin renders correctly.
It is also used to show the timeline, so this can appear at the top.
*/

import {
  useEffect,
  useRef,
  FC,
  useCallback,
  useState,
  MutableRefObject,
  useLayoutEffect,
} from 'react';
import {
  audioExtractionAPIResultAtom,
  categoriesAPIResultsAtom,
  curChannelsAtom,
  curTimeAtom,
  durationAtom,
  userCanEditAtom,
  waveSurferObjFamily,
} from 'packages/main/components/audio-tool/atoms';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  AudioAnnotationTypes,
  colorForAudioType,
  getSirenCategoryName,
  minLengthForRegion,
  NUM_MICROSEC_IN_A_SEC,
} from 'packages/main/components/audio-tool/audio-tool-types';
import { WaveSurferWrapper } from 'packages/main/components/audio-tool/wavesurfer-wrapper';
import type { Region } from 'wavesurfer.js/src/plugin/regions';
import { Button, Spin, Tooltip } from 'antd';
import { useInstallWaveSurferCallbacks } from 'packages/main/components/audio-tool/audio-tool-event-handlers';
import {
  AudioAnnotation,
  AudioExtraction,
} from 'packages/main/components/audio-tool/api';
import {
  devLog,
  getSirenCategoryTokenFromName,
  muteChannels,
  updateRegionColor,
} from 'packages/main/components/audio-tool/utils';
import type { WaveSurferBackend } from 'wavesurfer.js/types/backend';
import { LockOutlined } from '@ant-design/icons';
import CenteredBox from 'packages/lib/common-ui/centeredBox';
import type { Category } from 'packages/lib/api/annotations/contract/category';

type WaveSurferBackendWithBuffer = WaveSurferBackend & {
  buffer: string | undefined;
};

export const stylesWR: Record<string, React.CSSProperties> = {
  buttonContainer: { marginTop: 0, marginBottom: 0 },
  iconButton: { fontSize: '26px' },
  wavesurferRow: {
    display: 'grid',
    gridTemplateColumns: '90px 1fr 50px',
    alignItems: 'center', // TODO: Change to 'start' when doing Lisa's redesign to make text labels at top instead of middle vertically
  },
  gridRow: {
    display: 'grid',
    gridTemplateColumns: '50px 1fr 1fr',
    alignItems: 'center',
  },
  rowLabel: {
    textAlign: 'right',
    fontSize: '13px',
    paddingRight: '20px',
  },
  spinnerDiv: {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.25)',
    top: 0,
    left: 0,
    zIndex: 100,
    width: '100%',
    height: '100%',
  },
};

function createRegionOptions(
  startTimeInSec: number,
  endTimeInSec: number
): Region {
  return {
    start: startTimeInSec,
    end: endTimeInSec,
    loop: false,
    color: 'hsla(200, 50%, 70%, 0.4)',
    drag: false,
    resize: true, // BUG WORKAROUND: Must be true on creation. Can then be changed later.
  } as Region;
}

// Restore previously-created annotations
function addAnnotationsToWavesurfer(
  waveSurferObj: WaveSurfer,
  annotationType: AudioAnnotationTypes,
  annotations: AudioAnnotation[],
  theAudioExtraction: AudioExtraction,
  categoriesData: Category[],
  userCanEdit: boolean
): number {
  const sirenCategoryName = getSirenCategoryName(annotationType);
  const categoryToken = getSirenCategoryTokenFromName(
    sirenCategoryName,
    categoriesData
  );

  let numRegions = 0;
  annotations.forEach((annotation) => {
    if (annotation.categoryToken === categoryToken) {
      devLog(
        'Init: adding annotation loaded from backend: ',
        annotationType,
        annotation
      );
      numRegions++;
      const newRegion = waveSurferObj.addRegion(
        createRegionOptions(
          (annotation.startTimestamp - theAudioExtraction.startTimestamp) /
            NUM_MICROSEC_IN_A_SEC,
          (annotation.endTimestamp - theAudioExtraction.startTimestamp) /
            NUM_MICROSEC_IN_A_SEC
        )
      );
      newRegion.token = annotation.token ?? '';
      newRegion.annotationType = annotationType;
      updateRegionColor(newRegion, false, false, false);

      // BUG WORKAROUND: It seems that if you create a region (see addRegion above) with resize: false, it will NEVER allow
      // resizing, even if you change resize to true later. But if you create it with resize: true intiially, you CAN set it to
      // resize: false later (even here), and it can be toggled and will work as expected.
      newRegion.update({
        ...newRegion,
        resize: userCanEdit,
      });
    }
  });

  return numRegions;
}

function updateWaveColorForEditableState(
  waveSurferObj: WaveSurfer,
  userCanEdit: boolean
): void {
  const waveColors = userCanEdit
    ? colorForAudioType.UnlockedColors
    : colorForAudioType.LockedColors;
  if (waveColors && waveColors.waveColor && waveColors.progressColor) {
    waveSurferObj.setWaveColor(waveColors.waveColor); // color after the active (playing) cursor
    waveSurferObj.setProgressColor(waveColors.progressColor);
  }
}
interface WavesurferRowProps {
  audioURL?: string;
  audioBlob?: Blob;
  annotationType: AudioAnnotationTypes;
  audioAnnotations: AudioAnnotation[] | undefined;
  mute: boolean;
  showTimeline: boolean;
  height: number;
  width?: number;
  spectrogramContainerRef?: MutableRefObject<HTMLDivElement | null>;
}

export const WavesurferRow: FC<React.PropsWithChildren<WavesurferRowProps>> = ({
  audioURL,
  audioBlob,
  audioAnnotations,
  annotationType,
  spectrogramContainerRef,
  mute,
  showTimeline,
  height,
  width,
  children,
}) => {
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const waveSurferObj = useRecoilValue(waveSurferObjFamily(annotationType));
  const [numRegions, setNumRegions] = useState<number>(0); // Used to know if 'Clear Regions' button should be enabled or not
  const categories = useRecoilValue(categoriesAPIResultsAtom);
  const didInitRef = useRef(false);
  const installWaveSurferCallbacks = useInstallWaveSurferCallbacks();
  const audioExtraction = useRecoilValue(audioExtractionAPIResultAtom);
  const userCanEdit = useRecoilValue(userCanEditAtom);
  const setDuration = useSetRecoilState(durationAtom);
  const _curChannels = useRecoilValue(curChannelsAtom);
  const [prevCurChannels, setPrevCurChannels] = useState(_curChannels);
  const [isProcessing, setIsProcessing] = useState(true);
  const processingSpinnerRef = useRef(null);
  const [savedChannelBufferData, setSavedChannelBufferData] = useState<
    Array<Float32Array>
  >([]);
  const [mutedBufferData, setMutedBufferData] = useState<Float32Array>(
    new Float32Array(1)
  );

  useEffect(() => {
    if (waveSurferObj) {
      updateWaveColorForEditableState(waveSurferObj, userCanEdit);
    }
  }, [waveSurferObj, userCanEdit]);

  const handleAudioProcessEvent = useRecoilCallback(
    ({ set }) =>
      async (newCurTime: number): Promise<void> => {
        set(curTimeAtom, newCurTime);
      }
  );

  // Enable/disable the creation of new regions and editing existing regions
  useLayoutEffect(() => {
    if (waveSurferObj && waveSurferObj.regions) {
      if (!userCanEdit) {
        waveSurferObj.disableDragSelection();
      } else {
        waveSurferObj.enableDragSelection({ drag: false, resize: true });
      }

      for (const region of Object.values(waveSurferObj.regions.list)) {
        if (region.resize !== userCanEdit)
          region.update({
            ...region,
            resize: userCanEdit,
          });
      }
    }
  }, [waveSurferObj, userCanEdit]);

  useEffect(() => {
    if (waveSurferObj) {
      if (waveSurferObj.isDestroyed) {
        console.warn(
          "Can't load audio into WaveSurfer because it has been destroyed!!!",
          annotationType
        );
      }
      // Load audio into WaveSurfer only if it hasn't already been loaded. Otherwise, the Wavesurfer will re-render.
      if (!(waveSurferObj.backend as WaveSurferBackendWithBuffer).buffer) {
        if (audioURL) {
          waveSurferObj.load(audioURL);
        } else if (audioBlob) {
          waveSurferObj.loadBlob(audioBlob);
        }
      }
    }
  }, [waveSurferObj, audioURL, audioBlob, annotationType]);

  // Dynamically adjust wavesurfer heights as user changes window height
  useEffect(() => {
    if (waveSurferObj) {
      waveSurferObj.setHeight(height);
    }
  }, [waveSurferObj, height]);

  // Handle mute/unmute of channels when user changes which are muted
  useLayoutEffect(() => {
    if (
      waveSurferObj &&
      savedChannelBufferData.length > 0 &&
      prevCurChannels !== _curChannels
    ) {
      if (!isProcessing) {
        setIsProcessing(true);
      } // We must wait until the spinner has rendered in the DOM before we start the potentially long operation
      else if (processingSpinnerRef.current) {
        void muteChannels(
          waveSurferObj,
          savedChannelBufferData,
          mutedBufferData,
          _curChannels
        ).then(() => {
          setIsProcessing(false);
        });

        setPrevCurChannels(_curChannels);
      }
    }
  }, [
    waveSurferObj,
    savedChannelBufferData,
    mutedBufferData,
    _curChannels,
    prevCurChannels,
    isProcessing,
  ]);

  function saveBufferData(waveSurferObj: WaveSurfer): Promise<void> {
    return new Promise((resolve: (value: void) => void) => {
      // To mute channels, we zero out the buffer array data. To allow unmute, we must first save a copy of the original data.
      if (savedChannelBufferData.length === 0) {
        setIsProcessing(true);

        // We must do this for all channels, even muted ones, so the waveform changes
        const { buffer } = waveSurferObj.backend as WaveSurferBackend & {
          buffer: AudioBuffer;
        };
        const savedBufferData = new Array<Float32Array>(
          buffer.numberOfChannels
        );
        for (let n = 0; n < buffer.numberOfChannels; n++) {
          // IMPORTANT: We must deep copy the buffer when saving it. Otherwise, when we copy 0's into it later to mute it, our local copy will get 0'd as well!
          savedBufferData[n] = new Float32Array(buffer.getChannelData(n));
        }

        const zeroedData = new Float32Array(savedBufferData[0].length);
        zeroedData.fill(0);

        setSavedChannelBufferData(savedBufferData);
        setMutedBufferData(zeroedData);
        setIsProcessing(false);
      }

      resolve();
    });
  }

  // Initialize only once, when WaveSurfer and all related data is ready
  useEffect(() => {
    // Anything we can initialize *after* the WaveSurfer was created, we do, so that we can change those settings dynamically if those props change.
    // (vs. passing these to the Wavesurfer.create method, where any change would require destroying/recreating the Wavesurfer.)
    // Also bind WaveSurfer and Region plugin event callbacks.
    if (
      !didInitRef.current &&
      waveSurferObj &&
      audioAnnotations &&
      audioExtraction &&
      categories
    ) {
      devLog('Initializing wavesurfer:', annotationType);
      void installWaveSurferCallbacks(waveSurferObj, annotationType);

      updateWaveColorForEditableState(waveSurferObj, userCanEdit);
      waveSurferObj.setHeight(height);

      const numRegionsAdded = addAnnotationsToWavesurfer(
        waveSurferObj,
        annotationType,
        audioAnnotations,
        audioExtraction,
        categories,
        userCanEdit
      );
      setNumRegions(numRegionsAdded);

      waveSurferObj.on('region-update-end', (region: Region) => {
        setNumRegions(Object.keys(waveSurferObj.regions.list).length);
      });

      // Fires when audio is loaded and decoded.
      waveSurferObj.on('ready', async () => {
        if (waveSurferObj) {
          setDuration(waveSurferObj.getDuration());
          void saveBufferData(waveSurferObj);
        }
      });

      didInitRef.current = true;
    }

    // installWaveSurferCallbacks doesn't change in any meaningful way, but re-triggers the effect if included in deps array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    didInitRef.current,
    waveSurferObj,
    annotationType,
    audioAnnotations,
    addAnnotationsToWavesurfer,
    audioExtraction,
    categories,
    height,
    userCanEdit,
  ]);

  const handleClearRegionsButtonClick = useCallback((): void => {
    if (waveSurferObj) {
      waveSurferObj.clearRegions(); // Wavesurfer will send us an event when a region is cleared, which we use to delete it from the server
      setNumRegions(0);
    }
  }, [waveSurferObj]);

  // We have only one waveSurfer instance play audio. The rest are on mute, as they all shouldn't be playing the same audio clip.
  // We also have only one waveSurfer instance fire 'audioprocess' events, as again, more than one firing these would be redundant.
  useEffect(() => {
    if (waveSurferObj) {
      waveSurferObj.setMute(mute);

      // The audioprocess event is fired continuously as audio plays. Updates timeline.
      if (!mute) {
        waveSurferObj.on('audioprocess', handleAudioProcessEvent);
      } else {
        waveSurferObj.un('audioprocess', handleAudioProcessEvent);
      }
    }
  }, [waveSurferObj, mute, handleAudioProcessEvent]);

  // The NoAnnotations Wavesurfer row shows up only when no siren categories are enabled.
  const rowIsVisible = height > 0;

  return (
    <div style={{ position: 'relative' }}>
      {rowIsVisible && isProcessing && (
        <div style={stylesWR.spinnerDiv} ref={processingSpinnerRef}>
          <CenteredBox>
            <Spin size="large" />
          </CenteredBox>
        </div>
      )}
      <div style={stylesWR.wavesurferRow}>
        <div>
          {rowIsVisible && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div style={stylesWR.rowLabel}>
                {annotationType === AudioAnnotationTypes.NoAnnotations
                  ? 'Audio'
                  : annotationType}
              </div>
              {!userCanEdit && (
                <Tooltip title="This task is now read-only. If in QA mode, click Enable Editing to allow edits.">
                  <LockOutlined style={{ fontSize: '18px' }} />{' '}
                </Tooltip>
              )}
            </div>
          )}
        </div>

        <div>
          <WaveSurferWrapper
            annotationType={annotationType}
            normalize={true}
            regionMinLength={minLengthForRegion[annotationType]}
            timelineContainerRef={timelineRef}
            spectrogramContainerRef={spectrogramContainerRef}
            startingHeight={height}
            width={width}
          />
          <div
            ref={timelineRef}
            style={{
              width: '100%',
              display: showTimeline ? 'inherit' : 'none',
            }}
          />
        </div>

        <div
          style={{
            marginLeft: '1em',
          }}
        >
          {rowIsVisible && (
            <Tooltip title="Clear annotations">
              <Button
                icon={
                  <span className="material-icons" style={stylesWR.iconButton}>
                    clear
                  </span>
                }
                type="default"
                shape="circle"
                style={stylesWR.buttonContainer}
                onClick={handleClearRegionsButtonClick}
                disabled={numRegions === 0 || !userCanEdit}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
};
